// 偵測網格編輯器（SPEC #ls2505-334「網格操作」、DEV-06、W22）：NVR / Kinio Station 通道的 kinio_motion.zone_grid（editor: zone_grid）。
// - 底圖同偵測區域（先快照、按〔看即時〕才開即時影像），上面疊 18 列 × 22 格
// - 拖過的格子塗上或擦掉，起點那格的反相決定這一筆是塗或擦；「全選」「清除」；全 0 = 全畫面偵測
// - 儲存一次寫整個值；收斂後以 reported 重畫；有未存改動時離開要確認
// codec 在 @kinio/web-sdk zoneGrid。data-testid：zed_grid_editor、grid_cell_<row>_<col>（data-on）、grid_fill、grid_clear、grid_count（data-count）、
// zed_save、zed_close、zed_notice（data-kind）、zed_readonly。
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { GRID_COLS, GRID_ROWS, encodeZoneGrid, filledZoneGrid, paintCells, parseZoneGrid, strokeValue, zoneGridEqual, type ZoneGrid } from '@kinio/web-sdk';
import { useConfirm } from '../../../components/ConfirmDialog';
import type { VmsDevice } from '../../../lib/devices';
import { Backdrop } from './Backdrop';
import { useBackdrop } from './useBackdrop';
import './zed.css';

export interface GridSaveResult {
  error: string | null;
  reported?: unknown;
}

export interface GridEditorProps {
  dev: Pick<VmsDevice, 'apiEndpoint' | 'homeId' | 'aid'>;
  title: string;
  /** reported kinio_motion.zone_grid 原值 */
  reported: unknown;
  canSave: boolean;
  readOnlyReason?: string;
  onSave: (grid: ZoneGrid) => Promise<GridSaveResult>;
  onClose: () => void;
}

const DEFAULT_ASPECT = 16 / 9;
const countOn = (g: ZoneGrid) => g.reduce((n, row) => n + row.filter(Boolean).length, 0);

export function GridEditor({ dev, title, reported, canSave, readOnlyReason, onSave, onClose }: GridEditorProps) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const backdrop = useBackdrop(dev);
  const propBase = useMemo(() => parseZoneGrid(reported) ?? filledZoneGrid(false), [reported]);
  const baseKey = JSON.stringify(encodeZoneGrid(propBase));
  // 儲存收斂後的設備回報：設定查詢重抓回來之前先當基準（不然剛存完 dirty 還是 true）
  const [saved, setSaved] = useState<ZoneGrid | null>(null);
  const base = saved ?? propBase;
  const baseRef = useRef(base);
  const [grid, setGrid] = useState<ZoneGrid>(base);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'saved' | 'reported' | 'error'; text?: string } | null>(null);
  const [aspect, setAspect] = useState(DEFAULT_ASPECT);
  const stroke = useRef<{ value: boolean; base: ZoneGrid; cells: { row: number; col: number }[] } | null>(null);
  const savingRef = useRef(false);
  const alive = useRef(true);
  const editable = canSave && !saving;
  const dirty = !zoneGridEqual(grid, base);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    const prev = baseRef.current;
    baseRef.current = propBase;
    setSaved(null);
    if (savingRef.current) return;
    setGrid((g) => (zoneGridEqual(g, prev) ? propBase : g));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- baseKey 代表值
  }, [baseKey]);

  const requestClose = useCallback(async () => {
    if (savingRef.current) return;
    if (!dirty) return onClose();
    if (await ask({ danger: true, title: t('zed.discard_title'), body: t('zed.discard_body'), action: t('zed.discard') })) onClose();
  }, [dirty, onClose, ask, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[data-testid="confirm-dialog"]')) return;
      e.preventDefault();
      void requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const cellOf = (e: { clientX: number; clientY: number }) => {
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const c = el?.closest('[data-gcell]') as HTMLElement | null;
    if (!c) return null;
    const [row, col] = (c.dataset.gcell ?? '').split('_').map(Number);
    return Number.isInteger(row) && Number.isInteger(col) ? { row, col } : null;
  };
  const onDown = (e: ReactPointerEvent) => {
    if (!editable || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const c = cellOf(e);
    if (!c) return;
    e.preventDefault();
    const value = strokeValue(grid, c.row, c.col);
    stroke.current = { value, base: grid, cells: [c] };
    setGrid(paintCells(grid, [c], value));
    setNotice(null);
  };
  const onMove = (e: ReactPointerEvent) => {
    const s = stroke.current;
    if (!s) return;
    const c = cellOf(e);
    if (!c || s.cells.some((x) => x.row === c.row && x.col === c.col)) return;
    s.cells.push(c);
    setGrid(paintCells(s.base, s.cells, s.value));
  };
  const onUp = () => {
    stroke.current = null;
  };

  const save = async () => {
    if (!canSave || !dirty || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setNotice(null);
    const sent = grid;
    let res: GridSaveResult;
    try {
      res = await onSave(sent);
    } catch (e) {
      res = { error: e instanceof Error ? e.message : String(e) };
    }
    savingRef.current = false;
    if (!alive.current) return;
    setSaving(false);
    if (res.error) {
      setGrid(baseRef.current);
      setNotice({ kind: 'error', text: res.error });
      return;
    }
    const got = res.reported === undefined ? sent : (parseZoneGrid(res.reported) ?? baseRef.current);
    setGrid(got);
    setSaved(got);
    baseRef.current = got;
    setNotice({ kind: zoneGridEqual(got, sent) ? 'saved' : 'reported' });
  };

  const setAll = (value: boolean) => {
    if (!editable) return;
    setGrid(filledZoneGrid(value));
    setNotice(null);
  };
  const on = countOn(grid);
  const total = GRID_ROWS * GRID_COLS;

  return (
    <div className="zed-overlay" role="dialog" aria-modal="true" aria-label={title} data-testid="zed_grid_editor">
      <header className="zed-bar">
        <button type="button" className="btn" onClick={() => void requestClose()} disabled={saving} data-testid="zed_close">
          {t('zed.back')}
        </button>
        <h2 className="zed-title">{title}</h2>
        <div className="zed-actions">
          <button type="button" className="btn" onClick={() => setAll(true)} disabled={!editable} data-testid="grid_fill">
            {t('zed.grid_fill')}
          </button>
          <button type="button" className="btn" onClick={() => setAll(false)} disabled={!editable} data-testid="grid_clear">
            {t('zed.grid_clear')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!canSave || !dirty || saving} data-testid="zed_save">
            {saving ? t('zed.saving') : t('common.save')}
          </button>
        </div>
      </header>
      <div className="zed-stage">
        <div className="zed-media zed-readonly" style={{ '--zed-ar': String(aspect) } as CSSProperties}>
          <Backdrop b={backdrop} onAspect={setAspect} />
          <div
            className={`zed-grid${editable ? '' : ' zed-readonly'}`}
            role="grid"
            aria-label={title}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onPointerLeave={onUp}
          >
            {grid.map((row, r) =>
              row.map((v, c) => (
                <span
                  key={`${r}_${c}`}
                  role="gridcell"
                  aria-selected={v}
                  aria-label={t('zed.grid_cell', { row: r + 1, col: c + 1 })}
                  className={`zed-gcell${v ? ' on' : ''}`}
                  data-gcell={`${r}_${c}`}
                  data-on={v ? '1' : '0'}
                  data-testid={`grid_cell_${r}_${c}`}
                />
              )),
            )}
          </div>
        </div>
      </div>
      <footer className="zed-foot">
        <span data-testid="grid_count" data-count={on}>
          {on === 0 || on === total ? t('zed.grid_full_frame') : t('zed.grid_cells', { count: on, total })}
        </span>
        {!canSave ? (
          <span className="muted" role="status" data-testid="zed_readonly">
            {readOnlyReason ?? t('zed.read_only')}
          </span>
        ) : notice ? (
          <span className={notice.kind === 'error' ? 'error' : notice.kind === 'reported' ? 'warn' : 'muted'} role="status" data-testid="zed_notice" data-kind={notice.kind}>
            {notice.kind === 'error' ? `${notice.text} ${t('zed.reported_updated')}` : notice.kind === 'saved' ? t('zed.saved') : t('zed.reported_updated')}
          </span>
        ) : (
          <span className="muted">{t('zed.grid_hint')}</span>
        )}
      </footer>
      {dialog}
    </div>
  );
}
